import React, { useState, useEffect } from 'react';
import {
    Container, Stepper, Step, StepLabel, Box, Typography,
    Button, Paper
} from '@mui/material';
import BasicInfo from '../listing/BasicInfo';
import AmenitiesInfo from '../listing/AmenitiesInfo';
import LocationSelector from '../common/LocationSelector';
import ImageUpload from '../common/ImageUpload';
import { ERROR_MESSAGES } from '../../utils/errorMessages';
import { useNotification } from '../../contexts/NotificationContext';

const RoomForm = ({ initialValues = {}, onSubmit, isEdit = false, disabled = false }) => {
    const { showNotification } = useNotification();
    const [activeStep, setActiveStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        location: {
            prefecture: initialValues?.location?.prefecture || '',
            city: initialValues?.location?.city || '',
            station: initialValues?.location?.station || '',
            line: initialValues?.location?.line || '',
            walkingMinutes: initialValues?.location?.walkingMinutes || ''
        },
        rentamount: initialValues?.rentamount || '',
        roomDetails: {
            layout: initialValues?.roomDetails?.layout || '',
            size: initialValues?.roomDetails?.size || '',
            amenities: initialValues?.roomDetails?.amenities || []
        },
        images: initialValues?.images || [],
        description: initialValues?.description || '',
        availabilityType: initialValues?.availabilityType || 'medium',
        propertyType: initialValues?.propertyType || '',
        roomType: initialValues?.roomType || '',
        status: initialValues?.status || 'active',
        moveInTiming: initialValues?.moveInTiming || 'immediate'
    });

    const steps = ['基本情報', 'アメニティ', '写真アップロード'];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [activeStep]);

    const handleLocationChange = (location) => {
        setFormData(prev => ({
            ...prev,
            location: {
                ...prev.location,
                prefecture: location.prefecture?.name || '',
                city: location.city?.name || '',
                station: location.station?.name || '',
                line: location.line?.name || '',
                walkingMinutes: prev.location.walkingMinutes
            }
        }));
    };

    const handleBasicInfoChange = (basicInfo) => {
        setFormData(prev => ({
            ...prev,
            rentamount: basicInfo.rentamount,
            propertyType: basicInfo.propertyType,
            roomType: basicInfo.roomType,
            availabilityType: basicInfo.availabilityType,
            moveInTiming: basicInfo.moveInTiming,
            description: basicInfo.description,
            roomDetails: {
                ...prev.roomDetails,
                layout: basicInfo.roomDetails?.layout || '',
                size: basicInfo.roomDetails?.size || ''
            },
            location: {
                ...prev.location,
                walkingMinutes: basicInfo.location?.walkingMinutes || ''
            }
        }));
    };

    const handleAmenitiesChange = (amenities) => {
        setFormData(prev => ({
            ...prev,
            roomDetails: {
                ...prev.roomDetails,
                amenities
            }
        }));
    };

    const handleImageUpload = (urls) => {
        setFormData(prev => ({
            ...prev,
            images: urls
        }));
    };

    const handleNext = () => {
        const errors = validateStep(activeStep);
        if (Object.keys(errors).length === 0) {
            setActiveStep((prevStep) => prevStep + 1);
        } else {
            Object.values(errors).forEach(error => {
                showNotification(error, 'error');
            });
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await onSubmit(formData);
        } catch (error) {
            console.error('送信エラー:', error);
            showNotification(ERROR_MESSAGES.FORM.SUBMIT_ERROR, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>
                        <LocationSelector
                            onChange={handleLocationChange}
                            initialValues={formData.location}
                        />
                        <BasicInfo
                            onChange={handleBasicInfoChange}
                            value={formData}
                        />
                    </>
                );
            case 1:
                return (
                    <AmenitiesInfo
                        onChange={handleAmenitiesChange}
                        value={formData.roomDetails.amenities}
                    />
                );
            case 2:
                return (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                📸 魅力的な写真で申し込みを増やしましょう
                            </Typography>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    <Box component="li" sx={{ mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>必須の写真：</strong>建物の全体像、部屋の全体像、バス・トイレ
                                        </Typography>
                                    </Box>
                                    <Box component="li" sx={{ mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>撮影のコツ：</strong>
                                            自然光を活用し、明るく撮影。整理整頓して清潔感のある状態で撮影。
                                        </Typography>
                                    </Box>
                                    <Box component="li" sx={{ mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>アピールポイント：</strong>
                                            リフォーム箇所、新しい設備、共用部分の設備（宅配ボックス、駐輪場など）、収納スペース、キッチン、眺望
                                        </Typography>
                                    </Box>
                                    <Box component="li">
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>注意点：</strong>
                                            個人情報が写り込まないよう注意。写真は5枚まで登録可能です。
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                        <ImageUpload
                            value={formData.images}
                            onFileSelect={handleImageUpload}
                            variant="property"
                            multiple={true}
                            maxFiles={10}
                            title="物件写真"
                            uploadPath="rooms"
                        />
                    </>
                );
            default:
                return null;
        }
    };

    const validateStep = (step) => {
        const errors = {};
        switch (step) {
            case 0:
                if (!formData.rentamount) {
                    errors.rent = ERROR_MESSAGES.VALIDATION.ROOM.RENT;
                }
                if (!formData.location?.prefecture) {
                    errors.prefecture = '都道府県を選択してください';
                }
                if (!formData.location?.city) {
                    errors.city = '市区町村を選択してください';
                }
                if (!formData.location?.station) {
                    errors.station = '最寄り駅を選択してください';
                }
                if (!formData.location?.line) {
                    errors.line = '路線を選択してください';
                }
                if (!formData.location?.walkingMinutes) {
                    errors.walkingMinutes = '徒歩分数を入力してください';
                }
                if (!formData.propertyType) {
                    errors.propertyType = '物件タイプを選択してください';
                }
                if (!formData.roomType) {
                    errors.roomType = '部屋タイプを選択してください';
                }
                if (!formData.roomDetails?.layout) {
                    errors.layout = '間取りを入力してください';
                }
                if (!formData.roomDetails?.size) {
                    errors.size = '広さを入力してください';
                }
                break;
            case 1:
                break;
            case 2:
                if (!formData.images || formData.images.length === 0) {
                    errors.images = ERROR_MESSAGES.VALIDATION.ROOM.IMAGES.MIN_COUNT;
                }
                if (formData.images?.length > 5) {
                    errors.images = ERROR_MESSAGES.VALIDATION.ROOM.IMAGES.MAX_COUNT;
                }
                break;
        }
        return errors;
    };

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
                {isEdit ? '物件を編集' : '物件を登録'}
            </Typography>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            <Box>
                {renderStepContent(activeStep)}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    {activeStep > 0 && (
                        <Button onClick={handleBack} sx={{ mr: 1 }}>
                            戻る
                        </Button>
                    )}
                    {activeStep === steps.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={disabled || isSubmitting}
                        >
                            {isSubmitting ? '送信中...' : (isEdit ? '更新する' : '登録する')}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleNext}
                        >
                            次へ
                        </Button>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default RoomForm; 